import Hls from "hls.js";
import { P2PEngine, createP2PFragmentLoader, webrtcSupported } from "@p2p-hls/core-engine";
import { PlaybackMetrics } from "./playback-metrics.js";
import { Dashboard } from "./dashboard.js";
import { LiveSync } from "./sync.js";
import { watchUpload, watchConnection, type UploadPolicy } from "./network.js";
import { hlsDrmConfig, hasHlsDrm, type DrmConfig } from "./drm.js";
import { setupFairPlay } from "./fairplay.js";

/** How the stream is being played, and whether P2P is active. */
export type PlayerMode = "p2p" | "hls-only" | "native-hls" | "unsupported";

/** Coarse playback state for a user-facing shell (spinner / error / etc.). */
export type PlayerStatus = "loading" | "buffering" | "playing" | "recovering" | "error";

export interface P2PPlayerConfig {
  /** WebSocket URL of the tracker, e.g. "wss://tracker.example.com". */
  trackerUrl: string;
  /** Stream/swarm id — every viewer of the same channel shares this. */
  streamId: string;
  /** HLS manifest URL (.m3u8). */
  src: string;
  /** ICE servers. Provide a TURN server for production (STUN alone isn't enough). */
  iceServers?: RTCIceServer[];
  /** Mount the live dashboard into this element (optional). */
  dashboard?: HTMLElement | null;
  /** Join the swarm automatically on first `play` (default true). */
  autoActivate?: boolean;
  /**
   * Target live latency in seconds — how far behind the live edge every viewer
   * plays. All viewers sync to this same absolute position (via the stream's
   * PROGRAM-DATE-TIME) so they stay uniform. Also the P2P/prefetch window: larger
   * = more offload, smaller = closer to live. Default 24.
   */
  targetLatencySeconds?: number;
  /** @deprecated use targetLatencySeconds. */
  liveSyncDurationCount?: number;
  /** Interval (ms) to report metrics to the tracker's swarm dashboard (0 = off). */
  metricsReportIntervalMs?: number;
  /**
   * Scaling knobs, normally supplied by the tracker's GET /config (see
   * `fetchP2PConfig`) so an operator can tune the whole fleet centrally. Any left
   * unset fall back to the engine's DEFAULT_CONFIG.
   */
  maxPeers?: number;
  metricsSampleRate?: number;
  prefetchAhead?: number;
  maxConcurrentPrefetch?: number;
  peerRequestConcurrency?: number;
  /**
   * If set, the player periodically GETs `{configUrl}/config` and hot-reloads the
   * engine knobs on the running session (no page reload) — so an operator editing
   * knobs on the dashboard reaches live viewers too. Same base you pass to
   * `fetchP2PConfig`. iceServers are NOT hot-swapped (would need renegotiation).
   */
  configUrl?: string;
  /** Poll interval (ms) for `configUrl`. Default 30000; 0 disables. */
  configRefreshMs?: number;
  /**
   * Upload policy. "wifi-only" (default) stops serving to peers on a detected
   * metered/Data-Saver connection to protect the user's mobile data (this peer
   * still downloads from peers/CDN). "always" always uploads.
   */
  uploadPolicy?: UploadPolicy;
  /** Simulation-only geo hints for local multi-ISP testing. */
  geoSim?: { asn?: number; country?: string; region?: string };
  /**
   * ABR / buffer tuning (all optional; important on mobile / data-saver):
   *  - maxBitrate: cap auto-ABR to renditions ≤ this (bps).
   *  - startLevel: initial quality index (-1 = auto).
   *  - capLevelToPlayerSize: never pick a rendition larger than the <video> box.
   *  - maxBufferLength: seconds of forward buffer to hold.
   *  - adaptToConnection: scale maxPeers/prefetch to the link speed class (default true).
   */
  maxBitrate?: number;
  startLevel?: number;
  capLevelToPlayerSize?: boolean;
  maxBufferLength?: number;
  adaptToConnection?: boolean;
  /** Called when the effective playback mode is known. */
  onMode?: (mode: PlayerMode) => void;
  /**
   * Called on hls.js errors. `fatal` errors are auto-recovered where possible
   * (network → resume load, media → recover) up to a bounded retry count; use
   * this to surface a "reconnecting…"/"playback error" state to the user.
   */
  onError?: (info: { fatal: boolean; type: string; details: string }) => void;
  /** Coarse playback status for a UI shell (spinner / error banner / etc.). */
  onStatus?: (status: PlayerStatus) => void;
  /**
   * DRM (optional). Widevine/PlayReady run on the hls.js path; FairPlay runs on
   * the Safari native-HLS path. DRM coexists with P2P transparently (the loader
   * passes encrypted bytes through; the CDM decrypts after MSE). See ./drm.ts.
   */
  drm?: DrmConfig;
}

export interface P2PPlayer {
  /** The underlying engine (metrics, activate/deactivate). */
  readonly engine: P2PEngine | null;
  readonly mode: PlayerMode;
  /** Join the swarm now (if you set autoActivate=false). */
  activate(): void;
  /** Tear everything down. */
  destroy(): void;
}

/** Engine tuning knobs forwarded from P2PPlayerConfig → P2PEngine (all numeric). */
const ENGINE_KNOB_KEYS = [
  "metricsReportIntervalMs",
  "maxPeers",
  "metricsSampleRate",
  "prefetchAhead",
  "maxConcurrentPrefetch",
  "peerRequestConcurrency",
] as const;
type EngineKnobKey = (typeof ENGINE_KNOB_KEYS)[number];

/** Copy only the keys whose value is set (not null/undefined). */
function pickDefined(config: P2PPlayerConfig, keys: readonly EngineKnobKey[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const k of keys) {
    const v = config[k];
    if (v != null) out[k] = v;
  }
  return out;
}

/** The runtime config the tracker's GET /config returns. */
export interface P2PRuntimeConfig {
  iceServers: RTCIceServer[];
  engine: Partial<Record<EngineKnobKey, number>>;
}

/**
 * Fetch central runtime config from the tracker (GET {baseUrl}/config): the
 * iceServers to use (incl. short-lived TURN creds) and any engine tuning knobs.
 * Call this before `createP2PPlayer` and spread the result into its config, so
 * operators tune the fleet centrally instead of every client hardcoding TURN
 * creds. Throws on a failed fetch — the caller should fall back to STUN.
 */
export async function fetchP2PConfig(baseUrl: string): Promise<P2PRuntimeConfig> {
  const res = await fetch(baseUrl.replace(/\/+$/, "") + "/config");
  if (!res.ok) throw new Error(`p2p /config fetch failed: ${res.status}`);
  const data = (await res.json()) as Partial<P2PRuntimeConfig>;
  return { iceServers: data.iceServers ?? [], engine: data.engine ?? {} };
}

/**
 * Create a P2P-accelerated HLS player on an existing <video> element.
 *
 * P2P works on the hls.js path (custom fragment loader). Where hls.js is
 * unsupported but the browser plays HLS natively (Safari), it falls back to
 * plain HLS with no offload. Where WebRTC is unavailable, the engine degrades to
 * HTTP-only automatically. In every case the video still plays.
 */
export function createP2PPlayer(video: HTMLVideoElement, config: P2PPlayerConfig): P2PPlayer {
  const autoActivate = config.autoActivate ?? true;
  const iceServers = config.iceServers ?? [{ urls: "stun:stun.l.google.com:19302" }];

  // Native-HLS-only browsers (Safari) can't hook a segment loader → no P2P.
  // FairPlay DRM (if configured) is driven here, directly on the <video>.
  if (!Hls.isSupported()) {
    const canNative = video.canPlayType("application/vnd.apple.mpegurl") !== "";
    const mode: PlayerMode = canNative ? "native-hls" : "unsupported";
    let cleanupFairPlay: () => void = () => {};
    if (canNative) {
      if (config.drm?.fairplay) cleanupFairPlay = setupFairPlay(video, config.drm.fairplay);
      video.src = config.src;
    }
    config.onMode?.(mode);
    return {
      engine: null, mode, activate() {},
      destroy() { cleanupFairPlay(); video.removeAttribute("src"); },
    };
  }

  const engine = new P2PEngine({
    trackerUrl: config.trackerUrl,
    streamId: config.streamId,
    iceServers,
    geoSim: config.geoSim,
    // Forward only the knobs the caller actually set; the rest use DEFAULT_CONFIG.
    ...pickDefined(config, ENGINE_KNOB_KEYS),
  });

  const mode: PlayerMode = webrtcSupported() ? "p2p" : "hls-only";
  config.onMode?.(mode);

  // Target latency = the shared live position all viewers sync to (and the
  // P2P/prefetch window). hls.js holds it smoothly via playback-rate control;
  // LiveSync corrects absolute drift against the stream's PROGRAM-DATE-TIME.
  const targetLatency = config.targetLatencySeconds ?? config.liveSyncDurationCount ?? 24;

  const P2PLoader = createP2PFragmentLoader(engine, { getCurrentTime: () => video.currentTime });
  const hls = new Hls({
    fLoader: P2PLoader as unknown as Partial<typeof Hls.DefaultConfig>["fLoader"],
    liveSyncDuration: targetLatency,
    liveMaxLatencyDuration: targetLatency + 8,
    maxLiveSyncPlaybackRate: 1.06, // nudge playback rate to hold the target latency
    liveDurationInfinity: true,
    ...(config.capLevelToPlayerSize != null ? { capLevelToPlayerSize: config.capLevelToPlayerSize } : {}),
    ...(config.startLevel != null ? { startLevel: config.startLevel } : {}),
    ...(config.maxBufferLength != null ? { maxBufferLength: config.maxBufferLength } : {}),
    // Widevine/PlayReady via EME (encrypted media passes through the P2P loader,
    // the CDM decrypts after MSE append — DRM and P2P coexist). FairPlay is on
    // the native-HLS branch above.
    ...(hasHlsDrm(config.drm) ? hlsDrmConfig(config.drm!) : {}),
  } as unknown as Partial<typeof Hls.DefaultConfig>);

  // Fatal-error recovery: without this a single fatal network/media error ends
  // playback silently. Retry is bounded and the counters reset on a good fragment
  // so transient blips over a long session don't exhaust the budget.
  const emitStatus = (s: PlayerStatus): void => config.onStatus?.(s);
  // Forward-declared so the (async) error handler can trigger the SAME full
  // teardown as destroy(); assigned once all resources exist. `destroyed` guards
  // against double teardown (error path + a later destroy()).
  let destroyed = false;
  let teardown: () => void = () => {};
  let netRetries = 0;
  let mediaRetries = 0;
  hls.on(Hls.Events.ERROR, (_evt, data) => {
    config.onError?.({ fatal: !!data.fatal, type: String(data.type), details: String(data.details) });
    if (!data.fatal) return;
    if (data.type === Hls.ErrorTypes.NETWORK_ERROR && netRetries < 5) {
      netRetries += 1;
      emitStatus("recovering");
      hls.startLoad(); // resume loading the live playlist
    } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRetries < 3) {
      mediaRetries += 1;
      emitStatus("recovering");
      hls.recoverMediaError();
    } else {
      emitStatus("error");
      teardown(); // unrecoverable — tear the whole session down, not just hls
    }
  });
  hls.on(Hls.Events.FRAG_BUFFERED, () => { netRetries = 0; mediaRetries = 0; });

  // Cap auto-ABR to renditions at/below maxBitrate (mobile/data-saver). If none
  // qualifies, pin the lowest so we still honor the intent.
  if (config.maxBitrate != null) {
    const cap = config.maxBitrate;
    hls.on(Hls.Events.MANIFEST_PARSED, (_e, data) => {
      const levels = (data as { levels?: { bitrate: number }[] }).levels ?? [];
      let idx = -1;
      for (let i = 0; i < levels.length; i++) if (levels[i]!.bitrate <= cap) idx = i;
      hls.autoLevelCapping = idx >= 0 ? idx : 0;
    });
  }

  // Drive the coarse playback status from the media element for a UI shell.
  const onWaiting = (): void => emitStatus("buffering");
  const onPlaying = (): void => emitStatus("playing");
  video.addEventListener("waiting", onWaiting);
  video.addEventListener("stalled", onWaiting);
  video.addEventListener("playing", onPlaying);
  emitStatus("loading");

  hls.loadSource(config.src);
  hls.attachMedia(video);

  const liveSync = new LiveSync(hls, video, targetLatency);
  liveSync.start();

  // Save mobile data: stop uploading/serving on a detected metered connection.
  const unwatchUpload = watchUpload(config.uploadPolicy ?? "wifi-only", (en) => engine.setUploadEnabled(en));

  // Scale mesh aggressiveness to the link speed class (3G shouldn't hold 10 peers
  // + a deep prefetch window). Reuses the engine's live-knob path.
  const unwatchConn = config.adaptToConnection === false
    ? () => {}
    : watchConnection(
        // Baseline = the central config in effect at creation; connection adaptation
        // only reduces below this (poor links), never raises above it → no clobber.
        {
          maxPeers: config.maxPeers ?? 10,
          prefetchAhead: config.prefetchAhead ?? 12,
          maxConcurrentPrefetch: config.maxConcurrentPrefetch ?? 6,
          // Baseline anti-rebuffer knobs = DEFAULT_CONFIG (not exposed via
          // P2PPlayerConfig); the mobile profile only tightens these on weak links.
          httpOnlyDeadlineMs: 2500,
          hedgeAtDeadlineFraction: 0.5,
          peerRequestTimeoutMs: 4000,
          maxPeerRttMs: 0,
        },
        (knobs) => engine.updateConfig(knobs),
      );

  const playback = new PlaybackMetrics(video);
  let dashboard: Dashboard | null = null;
  if (config.dashboard) {
    dashboard = new Dashboard(engine, playback);
    dashboard.mount(config.dashboard);
  }

  const onPlay = () => engine.activate();
  if (autoActivate) video.addEventListener("play", onPlay, { once: true });

  // Hot-reload: poll /config and push knob changes into the running engine.
  let configTimer: ReturnType<typeof setInterval> | null = null;
  const refreshMs = config.configRefreshMs ?? 30_000;
  if (config.configUrl && refreshMs > 0) {
    const base = config.configUrl;
    configTimer = setInterval(() => {
      void fetchP2PConfig(base).then((c) => engine.updateConfig(c.engine)).catch(() => {});
    }, refreshMs);
  }

  teardown = () => {
    if (destroyed) return; // idempotent — error path and destroy() may both call
    destroyed = true;
    video.removeEventListener("play", onPlay);
    video.removeEventListener("waiting", onWaiting);
    video.removeEventListener("stalled", onWaiting);
    video.removeEventListener("playing", onPlaying);
    if (configTimer) clearInterval(configTimer);
    unwatchUpload();
    unwatchConn();
    liveSync.stop();
    dashboard?.stop();
    engine.deactivate();
    hls.destroy();
  };

  return {
    engine,
    mode,
    activate: () => engine.activate(),
    destroy: () => teardown(),
  };
}
